import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

export const OtpStep = ({ challenge, onVerify, onResend, onBack, error }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [wait, setWait] = useState(30);

  useEffect(() => {
    if (wait <= 0) { return undefined; }
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const submit = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    const ok = await onVerify(code);
    setLoading(false);
    if (!ok) setCode("");
  };

  const resend = async () => {
    if (await onResend()) { setWait(30); setCode(""); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="otp-form">
      <div className="flex items-start gap-3 text-sm text-slate-300 bg-slate-900/60 border border-slate-800 rounded-lg p-3">
        <MailCheck className="w-4 h-4 mt-0.5 text-primary shrink-0" />
        <span data-testid="otp-sent-to">We emailed a 6-digit code to <span className="font-mono text-white">{challenge.email_masked}</span>. It expires in 5 minutes.</span>
      </div>
      <div className="flex justify-center">
        <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={() => {}} autoFocus data-testid="otp-input">
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg text-white border-slate-700 bg-slate-900" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {error && (
        <div className="text-sm text-rose-400 bg-rose-950/40 border border-rose-900 rounded-md px-3 py-2" data-testid="otp-error">{error}</div>
      )}
      <Button type="submit" disabled={loading || code.length !== 6} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="otp-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & sign in"}
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors" data-testid="otp-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Use a different account
        </button>
        <button type="button" onClick={resend} disabled={wait > 0} className="text-primary disabled:text-slate-600 hover:underline" data-testid="otp-resend">
          {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
};
